import { Stack } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { Navigate, useNavigate, useOutletContext } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTextField, FormAutocomplete, PrimaryButton } from "../../../../../components/form";
import {
  createUserProfile,
  finishProfileSetup,
  getUserProfile,
} from "../../../../../api/profile";
import { ENUM_USER_ROLE } from "../../../../../enums";
import { ROUTE_PATHS } from "../../../../../routes/paths";
import { useAppSelector } from "../../../../../store";
import { useMutationWithDispatch } from "../../../../../hooks/useMutationWithDispatch";
import {
  moreAboutCompanySchema,
  MoreAboutCompanyFormData,
  PAGE_CONFIG,
} from "./config";
import {
  handleMoreAboutCompanySuccess,
  getCreateProfilePayload,
  getFinishProfileSetupPayload,
} from "./utils";
import {
  CreateProfileResponse,
  FinishProfileSetupResponse,
  OutletContext,
} from "./types";

function MoreAboutVendorCompanyForm() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const navigate = useNavigate();
  const userData = useAppSelector((state) => state.auth.user);
  const lists = useAppSelector((state) => state.lists);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<MoreAboutCompanyFormData>({
    resolver: zodResolver(moreAboutCompanySchema),
    defaultValues: {
      company: "",
      title: "",
      linkedIn: "",
      no_of_emp: "",
    },
  });

  const { mutateAsync: submitCreateProfile, isPending: creatingProfile } =
    useMutationWithDispatch<
      CreateProfileResponse,
      {
        company: string;
        title?: string;
        linkedIn?: string;
        no_of_emp: string | number;
      }
    >((payload) => createUserProfile(payload));

  const { mutateAsync: submitFinishSetup, isPending: finishingSetup } =
    useMutationWithDispatch<
      FinishProfileSetupResponse,
      { email: string; name: string }
    >((payload) => finishProfileSetup(payload));

  const { mutateAsync: refreshProfile, isPending: refreshingProfile } =
    useMutationWithDispatch<Awaited<ReturnType<ReturnType<typeof getUserProfile>>>, void>(
      () => getUserProfile()
    );

  const submitting = creatingProfile || finishingSetup || refreshingProfile;

  const defaultNoOfEmp = useMemo(
    () => lists?.no_of_emps?.data?.[0]?.id || "",
    [lists?.no_of_emps?.data]
  );

  const titlesOptions = useMemo(
    () =>
      (lists?.titles?.data || []).filter((item) =>
        item?.types?.includes(ENUM_USER_ROLE.VENDOR)
      ),
    [lists?.titles?.data]
  );

  const noOfEmpOptions = useMemo(
    () => lists?.no_of_emps?.data || [],
    [lists?.no_of_emps?.data]
  );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
  }, [setPageConfig]);

  const onSubmit = useCallback(
    async (data: MoreAboutCompanyFormData) => {
      try {
        const createProfilePayload = getCreateProfilePayload(
          data,
          defaultNoOfEmp
        );
        const createRes = await submitCreateProfile(createProfilePayload);

        if (!createRes?.success) {
          return;
        }

        const finishSetupPayload = getFinishProfileSetupPayload(
          userData?.email || "",
          data.company
        );
        await submitFinishSetup(finishSetupPayload);

        await refreshProfile();

        handleMoreAboutCompanySuccess(navigate);
      } catch (error) {
      }
    },
    [submitCreateProfile, submitFinishSetup, refreshProfile, navigate, defaultNoOfEmp, userData]
  );

  if (!localStorage.token || userData.role !== ENUM_USER_ROLE.VENDOR) {
    return <Navigate to={ROUTE_PATHS.AUTH.SIGNUP} replace />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack>
        <FormTextField
          name="company"
          control={control}
          label="Company Name"
          required
          error={errors.company?.message}
        />

        <FormAutocomplete
          name="title"
          control={control}
          label="Job Title"
          options={titlesOptions}
          loading={lists?.titles?.loading}
          freeSolo
          valueField="id"
          labelField="label"
          error={errors.title?.message}
        />

        <FormTextField
          name="linkedIn"
          control={control}
          label="LinkedIn URL"
          error={errors.linkedIn?.message}
          helperText="Enter your LinkedIn profile URL (e.g., https://www.linkedin.com/in/yourprofile)"
          formControlProps={{ sx: { mb: { xs: 4, lg: 0 } } }}
        />

        <FormAutocomplete
          name="no_of_emp"
          control={control}
          label="Number of Employees"
          required
          options={noOfEmpOptions}
          loading={lists?.no_of_emps?.loading}
          valueField="id"
          labelField="label"
          error={errors.no_of_emp?.message}
        />

        <PrimaryButton sx={{ mt: 2 }} loading={submitting}>Finish</PrimaryButton>
      </Stack>
    </form>
  );
}

export default MoreAboutVendorCompanyForm;
